import { useEffect, useRef, useState } from "react";
import { motion, useInView, useMotionValue, useScroll, useSpring, useTransform, useVelocity } from "framer-motion";
import { Pause, Play } from "lucide-react";
import useMotionPreference from "@/hooks/useMotionPreference";

const WORDS = ["Clean UI", "Maintainable Architecture", "Frontend Engineering", "Java Backend", "Performance First", "Continuous Learning", "Product Thinking", "Real-World Shipping"];

export default function EditorialMarquee() {
  const ref = useRef(null);
  const groupRef = useRef(null);
  const width = useRef(0);
  const offset = useRef(0);
  const speed = useRef(-32);
  const direction = useRef(-1);
  const [copies, setCopies] = useState(2);
  const [paused, setPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const reduceMotion = useMotionPreference();
  const inView = useInView(ref, { amount: 0.1 });
  const { scrollY } = useScroll();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const velocity = useVelocity(scrollY);
  const smoothVelocity = useSpring(velocity, { stiffness: 100, damping: 35 });
  const x = useMotionValue(0);
  const revealOpacity = useTransform(scrollYProgress, [0.05, 0.22, 0.83, 1], [0, 1, 1, 0.7]);
  const revealY = useTransform(scrollYProgress, [0.05, 0.22, 0.83, 1], [20, 0, 0, -10]);

  useEffect(() => {
    const measure = () => {
      if (!groupRef.current || !ref.current) return;
      width.current = groupRef.current.offsetWidth;
      setCopies(Math.max(2, Math.ceil(ref.current.offsetWidth / Math.max(1, width.current)) + 1));
    };
    const observer = new ResizeObserver(measure);
    observer.observe(groupRef.current);
    observer.observe(ref.current);
    measure();
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (reduceMotion || !inView || paused || hovered || focused) return;
    let frame;
    let previous;
    const tick = time => {
      const delta = previous ? Math.min(time - previous, 50) : 0;
      previous = time;
      if (!document.hidden && width.current) {
        const value = smoothVelocity.get();
        if (Math.abs(value) > 35) direction.current = value > 0 ? -1 : 1;
        const target = direction.current * Math.min(85, 32 + Math.abs(value) * 0.025);
        speed.current += (target - speed.current) * (1 - Math.exp(-delta / 180));
        offset.current += speed.current * delta / 1000;
        x.set((offset.current % width.current + width.current) % width.current - width.current);
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [inView, reduceMotion, paused, hovered, focused, smoothVelocity, x]);

  return (
    <motion.div ref={ref} data-testid="marquee" style={reduceMotion ? undefined : { opacity: revealOpacity, y: revealY }} className="group relative overflow-hidden border-y border-white/5 py-8" onPointerEnter={event => { if (event.pointerType === "mouse") setHovered(true); }} onPointerLeave={() => setHovered(false)} onFocusCapture={() => setFocused(true)} onBlurCapture={event => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false); }}>
      <div style={{ maskImage: "linear-gradient(to right, transparent, black 5%, black 95%, transparent)" }}>
        <motion.div data-testid="marquee-track" className="flex w-max" style={{ x: reduceMotion ? 0 : x }}>
          {Array.from({ length: copies }, (_, copy) => (
            <div ref={copy === 0 ? groupRef : undefined} key={copy} aria-hidden={copy > 0} className="flex shrink-0 items-center">
              {WORDS.map(word => <div key={word} className="flex shrink-0 items-center"><span className="mx-8 whitespace-nowrap font-mono text-sm uppercase tracking-[0.3em] text-white/35">{word}</span><span className="h-1.5 w-1.5 rounded-full bg-brand-500/60" /></div>)}
            </div>
          ))}
        </motion.div>
      </div>
      {!reduceMotion && <button type="button" aria-label={paused ? "Play marquee" : "Pause marquee"} aria-pressed={paused} onClick={() => setPaused(value => !value)} className="focus-ring absolute right-4 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-[#111214] text-white/60 transition-opacity sm:right-8 sm:opacity-0 sm:group-hover:opacity-100 sm:focus:opacity-100">{paused ? <Play aria-hidden="true" className="h-3.5 w-3.5" /> : <Pause aria-hidden="true" className="h-3.5 w-3.5" />}</button>}
    </motion.div>
  );
}
